import { type CSSProperties, useEffect, useState } from 'react';

import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import clsx from 'clsx';

import c from './_profileMap.module.scss';

export interface ProfileMapProps {
  latitude: string;
  longitude: string;
  city: string | null;
  zoom: number;
  googleMapsApiKey: string;
}

const CONTAINER_STYLE: CSSProperties = { width: '100%', height: '300px' };
// google.maps is the ambient global from @types/google.maps (not exported by @react-google-maps/api); see https://developers.google.com/maps/documentation/javascript/using-typescript
const DEFAULT_CENTER: google.maps.LatLngLiteral = { lat: -34.397, lng: 150.644 };
const MAP_OPTIONS: google.maps.MapOptions = {
  cameraControl: true,
  streetViewControl: true,
  zoomControl: true,
  fullscreenControl: false,
  scaleControl: true,
  gestureHandling: 'cooperative',
};

function resolveGeocodeQuery(latitude: string, longitude: string, city: string | null): string {
  if (latitude) return `${latitude}, ${longitude}`;
  return city ?? '';
}

export function ProfileMap({ latitude, longitude, city, zoom, googleMapsApiKey }: ProfileMapProps) {
  const { isLoaded } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey });
  const [center, setCenter] = useState(DEFAULT_CENTER);

  const query = resolveGeocodeQuery(latitude, longitude, city);

  useEffect(() => {
    if (!isLoaded || !query) return;
    const geocoder = new google.maps.Geocoder();
    geocoder
      .geocode({ address: query })
      .then(({ results }) => {
        if (results[0]) setCenter({ lat: results[0].geometry.location.lat(), lng: results[0].geometry.location.lng() });
      })
      .catch((err) => {
        console.error(err);
      });
  }, [isLoaded, query]);

  if (!isLoaded) return <div className={clsx('profile-map', c.map)} />;

  return (
    <div className={clsx('profile-map profile-map-controls', c.map, c.controls)}>
      <GoogleMap mapContainerStyle={CONTAINER_STYLE} center={center} zoom={zoom} options={MAP_OPTIONS}>
        <Marker position={center} />
      </GoogleMap>
    </div>
  );
}
